import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  useNavigate,
  useParams,
  useOutletContext,
  useLocation,
  useBlocker,
  useSearchParams,
} from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faStar,
  faPlus,
  faTrash,
  faTimes,
  faDollarSign,
  faTag,
  faFile,
  faUpload,
  faSpinner,
  faCheck,
  faChevronDown,
  faChevronUp,
  faExclamationTriangle,
  faArrowsRotate,
  faCheckCircle,
  faSave,
  faImage,
  faCloudArrowUp,
  faFileLines,
  faRobot,
  faClipboardCheck,
  faExpand,
  faCompress,
  faLayerGroup,
  faListOl,
} from '@fortawesome/free-solid-svg-icons';

import { api } from '@/lib/axios';
import { API_ROUTES, ROUTES, getAttributeTranslation, DEFAULT_CITY, DEFAULT_COUNTRY } from '@/constants';
import { useCountries, useCities, useDistricts } from '@/api/useLocations';
import { Button, Spinner } from '@/components/ui';
import { Input, Select } from '@/components/forms';
import { useModalStore } from '@/stores';
import type { ProposedCost, ProposedSpec, ProposedImage, ProviderProposal, ProviderContact, SavedDocument } from '@/types/provider';
import { SimilarProvidersModal, type SimilarProvider } from './SimilarProvidersModal';
import styles from './AddEditProvider.module.css';

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

interface CatalogService {
  id: string;
  name: string;
}
interface CatalogAttribute {
  id: string;
  name: string;
}
interface CatalogPricingUnit {
  id: string;
  name: string;
}
interface CatalogCategory {
  id: string;
  nombre: string;
}

interface Offering {
  id: string;
  service_details: CatalogService;
  description: string;
  specifications: Array<{ id?: string; attribute_details: CatalogAttribute; value: string }>;
  costs: Array<{
    id?: string;
    price: number;
    currency: string;
    description?: string;
    pricing_unit_details: CatalogPricingUnit;
  }>;
}

interface CostDraft {
  localId: string;
  pricingUnitId: string;
  price: string;
  currency: string;
  description: string;
}
interface SpecDraft {
  localId: string;
  attributeId: string;
  value: string;
}

interface ServiceFormState {
  serviceId: string;
  description: string;
  costs: CostDraft[];
  specs: SpecDraft[];
}

// Local DocumentAnalysis extends the shared type with the required serviceIdOverride
interface DocumentAnalysis {
  localId: string;
  file: File;
  status: 'uploading' | 'analyzing' | 'done' | 'error';
  gcsUrl?: string;
  filename: string;
  summary?: string;
  proposals?: ProviderProposal;
  reviewOpen: boolean;
  fieldAcceptance: Record<string, boolean>;
  fieldDismissed: Record<string, boolean>;
  serviceAcceptance: Record<string, boolean>;
  serviceIdOverride: Record<string, string>;
  error?: string;
  applied?: boolean;
  analysisStartTime?: number;
}
interface SavedImage {
  url: string;
  name: string;
  size_bytes: number;
  uploaded_at: string;
  source_document?: string;
}
interface PendingService {
  localId: string;
  serviceName: string;
  serviceId: string | null;
  selectedServiceId: string;
  description: string;
  costs: ProposedCost[];
  specs: ProposedSpec[];
}

function uid() {
  return Math.random().toString(36).slice(2);
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────

export function AddEditProvider() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModalStore();
  const { setHeaderSlot, setHeaderTitle } = useOutletContext<{
    setHeaderSlot: (node: React.ReactNode) => void;
    setHeaderTitle: (node: React.ReactNode) => void;
  }>();
  const isEdit = !!id;

  // ── Basic form state ──────────────────────────────────────────
  const [name, setName] = useState('');
  const [contacts, setContacts] = useState<ProviderContact[]>([
    { nombre: '', email: '', telefono: '', cargo: '', es_principal: true },
  ]);
  const [pais, setPais] = useState(DEFAULT_COUNTRY);
  const [ciudad, setCiudad] = useState(DEFAULT_CITY);
  const [distrito, setDistrito] = useState('');
  const [rating, setRating] = useState(0);
  const [providerState, setProviderState] = useState<'activo' | 'borrador'>('activo');
  const [savedProviderId, setSavedProviderId] = useState<string | null>(null);

  // ── Location API hooks (Country > City > District) ───────────
  const { data: countriesData } = useCountries();
  const { data: citiesData } = useCities(pais);
  const { data: districtsData } = useDistricts(pais, ciudad);
  const countryOptions = countriesData ?? [];
  const cityOptions = citiesData ?? [];
  const districtOptions = districtsData ?? [];

  const [originalValues, setOriginalValues] = useState({
    name: '',
    contacts: [{ nombre: '', email: '', telefono: '', cargo: '', es_principal: true }] as ProviderContact[],
    pais: DEFAULT_COUNTRY,
    distrito: '',
    ciudad: DEFAULT_CITY,
    rating: 0,
    state: 'activo' as 'activo' | 'borrador',
  });
  const hasFormChanges =
    name !== originalValues.name ||
    JSON.stringify(contacts) !== JSON.stringify(originalValues.contacts) ||
    pais !== originalValues.pais ||
    distrito !== originalValues.distrito ||
    ciudad !== originalValues.ciudad ||
    rating !== originalValues.rating ||
    providerState !== originalValues.state;

  const addContact = () => {
    setContacts((prev) => [...prev, { nombre: '', email: '', telefono: '', cargo: '', es_principal: false }]);
  };

  const removeContact = (index: number) => {
    setContacts((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (!next.some((c) => c.es_principal) && next.length > 0) {
        next[0] = { ...next[0], es_principal: true };
      }
      return next;
    });
  };

  const updateContact = (index: number, field: keyof ProviderContact, value: string | boolean) => {
    setContacts((prev) =>
      prev.map((c, i) => {
        if (i !== index) {
          if (field === 'es_principal' && value === true) return { ...c, es_principal: false };
          return c;
        }
        return { ...c, [field]: value };
      }),
    );
  };

  // ── Saved documents (edit mode) ───────────────────────────────
  const [savedDocuments, setSavedDocuments] = useState<SavedDocument[]>([]);

  // ── Provider images ─────────────────────────────────────────
  const [savedImages, setSavedImages] = useState<SavedImage[]>([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  // ── Pending services (from proposals, not yet saved) ──────────
  const [pendingServices, setPendingServices] = useState<PendingService[]>([]);
  const [expandedPending, setExpandedPending] = useState<Set<string>>(new Set());

  // ── Inline service form ───────────────────────────────────────
  const serviceFormRef = useRef<HTMLDivElement>(null);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [editingOfferingId, setEditingOfferingId] = useState<string | null>(null);
  const [serviceForm, setServiceForm] = useState<ServiceFormState>({
    serviceId: '',
    description: '',
    costs: [{ localId: uid(), pricingUnitId: '', price: '', currency: 'PEN', description: '' }],
    specs: [],
  });
  const [isCreatingService, setIsCreatingService] = useState(false);
  const [newServiceName, setNewServiceName] = useState('');
  const [newServiceCategory, setNewServiceCategory] = useState('');
  const [creatingAttrIndex, setCreatingAttrIndex] = useState<number | null>(null);
  const [newAttrName, setNewAttrName] = useState('');

  // ── Document analysis state ───────────────────────────────────
  const [documents, setDocuments] = useState<DocumentAnalysis[]>([]);
  const [contextText, setContextText] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  // ── Similar providers (from first document analysis) ──────────
  const [similarProviders, setSimilarProviders] = useState<SimilarProvider[]>([]);

  // ── Sidebar expanded mode ─────────────────────────────────────
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // ── Saving state ──────────────────────────────────────────────
  const [isSaving, setIsSaving] = useState(false);

  // ── Queries ───────────────────────────────────────────────────

  const { data: provider, isLoading: isLoadingProvider } = useQuery({
    queryKey: ['provider', id],
    queryFn: async () => {
      const res = await api.get(`${API_ROUTES.providers}${id}/`);
      return res.data;
    },
    enabled: isEdit,
  });

  const activeProviderId = id ?? savedProviderId;

  const { data: offeringsData } = useQuery({
    queryKey: ['offerings', activeProviderId],
    queryFn: async () => {
      const res = await api.get(`${API_ROUTES.offerings}?provider=${activeProviderId}`);
      return (res.data.results || []) as Offering[];
    },
    enabled: !!activeProviderId,
  });
  const offerings: Offering[] = useMemo(() => offeringsData ?? [], [offeringsData]);

  const { data: catalogServices = [] } = useQuery<CatalogService[]>({
    queryKey: ['catalog-services'],
    queryFn: async () => {
      const res = await api.get(API_ROUTES.services);
      return Array.isArray(res.data) ? res.data : res.data.results || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: pricingUnits = [] } = useQuery<CatalogPricingUnit[]>({
    queryKey: ['pricing-units'],
    queryFn: async () => {
      const res = await api.get(API_ROUTES.pricingUnits);
      return Array.isArray(res.data) ? res.data : res.data.results || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: allAttributes = [] } = useQuery<CatalogAttribute[]>({
    queryKey: ['attributes'],
    queryFn: async () => {
      const res = await api.get(API_ROUTES.attributes);
      return Array.isArray(res.data) ? res.data : res.data.results || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const { data: categories = [] } = useQuery<CatalogCategory[]>({
    queryKey: ['categories'],
    queryFn: async () => {
      const res = await api.get(API_ROUTES.categories);
      return Array.isArray(res.data) ? res.data : res.data.results || [];
    },
    enabled: isCreatingService,
    staleTime: 5 * 60 * 1000,
  });

  // Populate form from existing provider
  useEffect(() => {
    if (provider) {
      const loadedContacts: ProviderContact[] = provider.contacts?.length
        ? provider.contacts.map((c: ProviderContact) => ({
            id: c.id,
            nombre: c.nombre || '',
            email: c.email || '',
            telefono: c.telefono || '',
            cargo: c.cargo || '',
            es_principal: c.es_principal || false,
          }))
        : [{ nombre: '', email: provider.email || '', telefono: provider.telefono || '', cargo: '', es_principal: true }];

      const loadedPais = provider.pais || DEFAULT_COUNTRY;
      const loadedDistrito = provider.distrito || '';

      const vals = {
        name: provider.nombre_comercial || '',
        contacts: loadedContacts,
        pais: loadedPais,
        distrito: loadedDistrito,
        ciudad: provider.ciudad || DEFAULT_CITY,
        rating: provider.calificacion || 0,
        state: (provider.state === 'activo' ? 'activo' : 'borrador') as 'activo' | 'borrador',
      };
      setName(vals.name);
      setContacts(loadedContacts);
      setPais(vals.pais);
      setDistrito(vals.distrito);
      setCiudad(vals.ciudad);
      setRating(vals.rating);
      setProviderState(vals.state);
      setOriginalValues(vals);
      setSavedProviderId(provider.id);
      setSavedDocuments(provider.documents || []);
      setSavedImages(provider.images || []);
    }
  }, [provider]);

  // ── Inject documents carried from create → edit redirect ─────
  useEffect(() => {
    const state = location.state as { pendingDocuments?: DocumentAnalysis[] } | null;
    if (state?.pendingDocuments && state.pendingDocuments.length > 0) {
      setDocuments((prev) => [...prev, ...state.pendingDocuments!]);
      // Clear location state so it doesn't re-apply on re-render
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  // ── Auto-open offering for editing (from details page link) ──
  const offeringParam = searchParams.get('offering');
  const [autoOpenApplied, setAutoOpenApplied] = useState<string | null>(null);
  useEffect(() => {
    if (!offeringParam || !offerings.length || autoOpenApplied === offeringParam) return;
    const offering = offerings.find((o) => o.id === offeringParam);
    if (offering) {
      startEditOffering(offering);
      setAutoOpenApplied(offeringParam);
      // Clean the URL param so it doesn't re-trigger
      navigate(location.pathname, { replace: true });
    }
  }, [offeringParam, offerings, autoOpenApplied, navigate, location.pathname]);

  // ── Mutations ─────────────────────────────────────────────────

  const createServiceMutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = { name: newServiceName };
      if (newServiceCategory) payload.category_ids = [newServiceCategory];
      const res = await api.post(API_ROUTES.services, payload);
      return res.data;
    },
    onSuccess: (svc) => {
      queryClient.invalidateQueries({ queryKey: ['catalog-services'] });
      toast.success('Service created');
      setIsCreatingService(false);
      setNewServiceName('');
      setNewServiceCategory('');
      setServiceForm((f) => ({ ...f, serviceId: svc.id.toString() }));
    },
    onError: () => toast.error('Error creating service'),
  });

  const createAttributeMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(API_ROUTES.attributes, { name: newAttrName, input_type: 'text' });
      return res.data;
    },
    onSuccess: (attr) => {
      queryClient.invalidateQueries({ queryKey: ['attributes'] });
      toast.success('Attribute created');
      if (creatingAttrIndex !== null) {
        setServiceForm((f) => ({
          ...f,
          specs: f.specs.map((s, i) =>
            i === creatingAttrIndex ? { ...s, attributeId: attr.id.toString() } : s,
          ),
        }));
        setCreatingAttrIndex(null);
      }
      setNewAttrName('');
    },
    onError: () => toast.error('Error creating attribute'),
  });

  const deleteOfferingMutation = useMutation({
    mutationFn: async (offeringId: string) => {
      await api.delete(`${API_ROUTES.offerings}${offeringId}/`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offerings', activeProviderId] });
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      toast.success('Service removed');
    },
    onError: () => toast.error('Error removing service'),
  });

  // ── Service form helpers ──────────────────────────────────────

  const resetServiceForm = () => {
    setServiceForm({
      serviceId: '',
      description: '',
      costs: [{ localId: uid(), pricingUnitId: '', price: '', currency: 'PEN', description: '' }],
      specs: [],
    });
    setShowServiceForm(false);
    setEditingOfferingId(null);
    setIsCreatingService(false);
    setNewServiceName('');
    setNewServiceCategory('');
    setCreatingAttrIndex(null);
    setNewAttrName('');
  };

  const startEditOffering = (o: Offering) => {
    setServiceForm({
      serviceId: o.service_details.id.toString(),
      description: o.description || '',
      costs: o.costs.length
        ? o.costs.map((c) => ({
            localId: uid(),
            pricingUnitId: c.pricing_unit_details.id.toString(),
            price: c.price.toString(),
            currency: c.currency,
            description: c.description || '',
          }))
        : [{ localId: uid(), pricingUnitId: '', price: '', currency: 'PEN', description: '' }],
      specs: o.specifications.map((s) => ({
        localId: uid(),
        attributeId: s.attribute_details.id.toString(),
        value: s.value,
      })),
    });
    setEditingOfferingId(o.id);
    setShowServiceForm(true);
    // Scroll to service form after render
    setTimeout(
      () => serviceFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      100,
    );
  };

  const addCost = () =>
    setServiceForm((f) => ({
      ...f,
      costs: [
        ...f.costs,
        { localId: uid(), pricingUnitId: '', price: '', currency: 'PEN', description: '' },
      ],
    }));
  const removeCost = (localId: string) =>
    setServiceForm((f) => ({ ...f, costs: f.costs.filter((c) => c.localId !== localId) }));
  const updateCost = (localId: string, field: keyof CostDraft, value: string) =>
    setServiceForm((f) => ({
      ...f,
      costs: f.costs.map((c) => (c.localId === localId ? { ...c, [field]: value } : c)),
    }));

  const addSpec = () =>
    setServiceForm((f) => ({
      ...f,
      specs: [...f.specs, { localId: uid(), attributeId: '', value: '' }],
    }));
  const removeSpec = (localId: string) =>
    setServiceForm((f) => ({ ...f, specs: f.specs.filter((s) => s.localId !== localId) }));
  const updateSpec = (localId: string, field: keyof SpecDraft, value: string) =>
    setServiceForm((f) => ({
      ...f,
      specs: f.specs.map((s) => (s.localId === localId ? { ...s, [field]: value } : s)),
    }));

  const handleSubmitServiceForm = async () => {
    if (!serviceForm.serviceId) {
      toast.error('Select a service');
      return;
    }
    const theProviderId = activeProviderId;
    if (!theProviderId) {
      toast.error('Save the provider first');
      return;
    }
    const payload = {
      provider: theProviderId,
      service_id: serviceForm.serviceId,
      description: serviceForm.description,
      costs: serviceForm.costs
        .filter((c) => c.price && c.pricingUnitId)
        .map((c) => ({
          pricing_unit_id: c.pricingUnitId,
          price: parseFloat(c.price),
          currency: c.currency,
          description: c.description,
        })),
      specifications: serviceForm.specs
        .filter((s) => s.attributeId && s.value)
        .map((s) => ({
          attribute_id: s.attributeId,
          value: s.value,
        })),
    };
    try {
      if (editingOfferingId) {
        await api.patch(`${API_ROUTES.offerings}${editingOfferingId}/`, payload);
        toast.success('Service updated');
      } else {
        await api.post(API_ROUTES.offerings, payload);
        toast.success('Service added');
      }
      queryClient.invalidateQueries({ queryKey: ['offerings', theProviderId] });
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      resetServiceForm();
    } catch {
      toast.error('Error saving service');
    }
  };

  // ── Document analysis ─────────────────────────────────────────

  const analyzeDocument = useCallback(
    async (localId: string, file: File) => {
      setDocuments((prev) =>
        prev.map((d) =>
          d.localId === localId ? { ...d, status: 'analyzing', analysisStartTime: Date.now() } : d,
        ),
      );
      try {
        const formData = new FormData();
        formData.append('file', file);
        if (contextText) formData.append('additional_context', contextText);
        const res = await api.post<{
          gcs_url: string;
          filename: string;
          summary: string;
          proposals: ProviderProposal;
          similar_providers?: SimilarProvider[];
          proposed_images?: ProposedImage[];
        }>(API_ROUTES.analyzeProviderDocument, formData);
        const { gcs_url, filename, summary, proposals, similar_providers, proposed_images } =
          res.data;
        const fieldAcceptance: Record<string, boolean> = {};
        if (proposals.nombre_comercial) fieldAcceptance.nombre_comercial = false;
        if (proposals.email) fieldAcceptance.email = false;
        if (proposals.telefono) fieldAcceptance.telefono = false;
        if (proposals.pais) fieldAcceptance.pais = false;
        if (proposals.distrito) fieldAcceptance.distrito = false;
        if (proposals.ciudad) fieldAcceptance.ciudad = false;
        if (proposals.calificacion != null) fieldAcceptance.calificacion = false;
        const serviceAcceptance: Record<number, boolean> = {};
        proposals.services.forEach((_, i) => {
          serviceAcceptance[i] = true;
        });
        setDocuments((prev) =>
          prev.map((d) =>
            d.localId === localId
              ? {
                  ...d,
                  status: 'done',
                  gcsUrl: gcs_url,
                  filename,
                  summary,
                  proposals,
                  fieldAcceptance,
                  serviceAcceptance,
                  reviewOpen: true,
                }
              : d,
          ),
        );

        // Auto-add services as phantoms if no existing or queued services
        const hasExistingServices = (offeringsData ?? []).length > 0;
        setPendingServices((currentPending) => {
          if (hasExistingServices || currentPending.length > 0) return currentPending;
          const newPending = (proposals.services || []).map((svc) => ({
            localId: uid(),
            serviceName: svc.service_name,
            serviceId: svc.service_id,
            selectedServiceId: svc.service_id ? svc.service_id.toString() : '',
            description: svc.description,
            costs: svc.costs || [],
            specs: svc.specs || [],
          }));
          if (newPending.length > 0) {
            toast.success(
              `${newPending.length} service${newPending.length > 1 ? 's' : ''} found — review in Services below`,
            );
          }
          return [...currentPending, ...newPending];
        });

        // Auto-add venue images extracted from the document
        if (proposed_images && proposed_images.length > 0) {
          setSavedImages((prev) => {
            const existingUrls = new Set(prev.map((img) => img.url));
            const newImages = proposed_images
              .filter((img) => !existingUrls.has(img.url))
              .map((img) => ({
                url: img.url,
                name: img.name,
                size_bytes: img.size_bytes,
                uploaded_at: img.uploaded_at,
                source_document: img.source_document,
              }));
            if (newImages.length > 0) {
              toast.success(
                `${newImages.length} venue image${newImages.length > 1 ? 's' : ''} extracted from document`,
              );
            }
            return [...prev, ...newImages];
          });
        }

        // Show similar providers warning (only in create mode, first analysis)
        if (!isEdit && similar_providers && similar_providers.length > 0) {
          setSimilarProviders(similar_providers);
        }
      } catch (err: unknown) {
        const anyErr = err as { response?: { data?: { error?: string } } };
        const msg = anyErr?.response?.data?.error || 'Failed to analyze document';
        setDocuments((prev) =>
          prev.map((d) =>
            d.localId === localId
              ? { ...d, status: 'error', error: msg }
              : d,
          ),
        );
      }
    },
    [contextText, isEdit, offeringsData],
  );

  const handleBatchAnalyze = useCallback(
    async (files: File[]) => {
      const localId = uid();
      setDocuments((prev) => [
        ...prev,
        {
          localId,
          file: files[0],
          status: 'analyzing' as const,
          filename: `Batch (${files.length} files)`,
          reviewOpen: false,
          fieldAcceptance: {},
          fieldDismissed: {},
          serviceAcceptance: {},
          serviceIdOverride: {},
          analysisStartTime: Date.now(),
        },
      ]);

      try {
        const formData = new FormData();
        for (const file of files) {
          formData.append('files', file);
        }
        if (contextText) formData.append('additional_context', contextText);

        const res = await api.post<{
          documents: Array<{ gcs_url: string; filename: string; size_bytes: number }>;
          summary: string;
          proposals: ProviderProposal;
          similar_providers?: SimilarProvider[];
        }>(API_ROUTES.analyzeBatchDocuments, formData);

        const { documents: uploadedDocs, summary, proposals, similar_providers } = res.data;

        const fieldAcceptance: Record<string, boolean> = {};
        if (proposals.nombre_comercial) fieldAcceptance.nombre_comercial = false;
        if (proposals.email) fieldAcceptance.email = false;
        if (proposals.telefono) fieldAcceptance.telefono = false;
        if (proposals.pais) fieldAcceptance.pais = false;
        if (proposals.distrito) fieldAcceptance.distrito = false;
        if (proposals.ciudad) fieldAcceptance.ciudad = false;
        if (proposals.calificacion != null) fieldAcceptance.calificacion = false;
        const serviceAcceptance: Record<number, boolean> = {};
        proposals.services.forEach((_, i) => {
          serviceAcceptance[i] = true;
        });

        setDocuments((prev) =>
          prev.map((d) =>
            d.localId === localId
              ? {
                  ...d,
                  status: 'done',
                  gcsUrl: uploadedDocs[0]?.gcs_url,
                  summary,
                  proposals,
                  fieldAcceptance,
                  serviceAcceptance,
                  reviewOpen: true,
                }
              : d,
          ),
        );

        // Save all document references
        const newSavedDocs: SavedDocument[] = uploadedDocs.map((doc) => ({
          url: doc.gcs_url,
          name: doc.filename,
          size_bytes: doc.size_bytes,
          uploaded_at: new Date().toISOString(),
        }));
        setSavedDocuments((prev) => [...prev, ...newSavedDocs]);

        // Auto-add services as phantoms if no existing services
        const hasExistingServices = (offeringsData ?? []).length > 0;
        setPendingServices((currentPending) => {
          if (hasExistingServices || currentPending.length > 0) return currentPending;
          const newPending = (proposals.services || []).map((svc) => ({
            localId: uid(),
            serviceName: svc.service_name,
            serviceId: svc.service_id,
            selectedServiceId: svc.service_id ? svc.service_id.toString() : '',
            description: svc.description,
            costs: svc.costs || [],
            specs: svc.specs || [],
          }));
          if (newPending.length > 0) {
            toast.success(
              `${newPending.length} service${newPending.length > 1 ? 's' : ''} found — review in Services below`,
            );
          }
          return [...currentPending, ...newPending];
        });

        if (!isEdit && similar_providers && similar_providers.length > 0) {
          setSimilarProviders(similar_providers);
        }
      } catch (err: unknown) {
        const anyErr = err as { response?: { data?: { error?: string } } };
        const msg = anyErr?.response?.data?.error || 'Failed to analyze documents';
        setDocuments((prev) =>
          prev.map((d) => (d.localId === localId ? { ...d, status: 'error', error: msg } : d)),
        );
      }
    },
    [contextText, isEdit, offeringsData],
  );

  const handleSequentialAnalyze = (files: File[]) => {
    const newDocs = files.map((file) => ({
      localId: uid(),
      file,
      status: 'uploading' as const,
      filename: file.name,
      reviewOpen: false,
      fieldAcceptance: {} as Record<string, boolean>,
      fieldDismissed: {} as Record<string, boolean>,
      serviceAcceptance: {} as Record<number, boolean>,
      serviceIdOverride: {} as Record<number, string>,
    }));
    setDocuments((prev) => [...prev, ...newDocs]);
    for (const doc of newDocs) {
      analyzeDocument(doc.localId, doc.file);
    }
  };

  const handleFilesAdded = (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (arr.length === 0) return;

    // Single file — existing behavior
    if (arr.length === 1) {
      const file = arr[0];
      if (documents.length >= 20) {
        toast.error('Maximum 20 documents allowed');
        return;
      }
      const localId = uid();
      setDocuments((prev) => [
        ...prev,
        {
          localId,
          file,
          status: 'uploading',
          filename: file.name,
          reviewOpen: false,
          fieldAcceptance: {},
          fieldDismissed: {},
          serviceAcceptance: {},
          serviceIdOverride: {},
        },
      ]);
      analyzeDocument(localId, file);
      return;
    }

    // Multiple files
    const maxNew = 20 - documents.length;
    const validFiles = arr.slice(0, maxNew);
    if (validFiles.length === 0) {
      toast.error('Maximum 20 documents reached');
      return;
    }

    openModal(
      <div className={styles.unsavedModal}>
        <div
          className={styles.unsavedModalIcon}
          style={{ background: '#DBEAFE', color: '#2563EB' }}
        >
          <FontAwesomeIcon icon={faFileLines} />
        </div>
        <h3 className={styles.unsavedModalTitle}>{validFiles.length} Documents Selected</h3>
        <p className={styles.unsavedModalText}>How would you like to analyze these documents?</p>
        <div className={styles.batchChoiceCards}>
          <button
            className={styles.batchChoiceCard}
            onClick={() => {
              closeModal();
              handleBatchAnalyze(validFiles);
            }}
          >
            <FontAwesomeIcon icon={faLayerGroup} className={styles.batchChoiceIcon} />
            <span className={styles.batchChoiceTitle}>Analyze as batch</span>
            <span className={styles.batchChoiceDesc}>
              Merge all docs into one analysis. Best when all belong to the same provider.
            </span>
          </button>
          <button
            className={styles.batchChoiceCard}
            onClick={() => {
              closeModal();
              handleSequentialAnalyze(validFiles);
            }}
          >
            <FontAwesomeIcon icon={faListOl} className={styles.batchChoiceIcon} />
            <span className={styles.batchChoiceTitle}>Analyze separately</span>
            <span className={styles.batchChoiceDesc}>
              Review each document one at a time. Best when docs are from different providers.
            </span>
          </button>
        </div>
      </div>,
    );
  };

  const handleApplyField = (fieldKey: string, value: string | number | null) => {
    switch (fieldKey) {
      case 'nombre_comercial':
        setName((value as string) || '');
        break;
      case 'pais':
        setPais((value as string) || DEFAULT_COUNTRY);
        break;
      case 'distrito':
        setDistrito((value as string) || '');
        break;
      case 'ciudad':
        setCiudad((value as string) || DEFAULT_CITY);
        break;
      case 'calificacion':
        setRating((value as number) || 0);
        break;
    }
  };

  const handleApplyAll = async (doc: DocumentAnalysis) => {
    if (!doc.proposals) return;
    const { proposals, fieldAcceptance, serviceAcceptance } = doc;

    // 1. Apply all non-dismissed basic fields to form state
    const fieldDismissed = doc.fieldDismissed || {};
    if (!fieldDismissed.nombre_comercial && proposals.nombre_comercial)
      setName(proposals.nombre_comercial);
    if (proposals.contactos?.length) {
      setContacts(proposals.contactos.map((c, i) => ({
        nombre: c.nombre || '',
        email: c.email || '',
        telefono: c.telefono || '',
        cargo: c.cargo || '',
        es_principal: i === 0,
      })));
    } else if (proposals.email || proposals.telefono) {
      setContacts([{
        nombre: '',
        email: proposals.email || '',
        telefono: proposals.telefono || '',
        cargo: '',
        es_principal: true,
      }]);
    }
    if (!fieldDismissed.pais && proposals.pais) setPais(proposals.pais);
    if (!fieldDismissed.distrito && proposals.distrito) setDistrito(proposals.distrito);
    if (!fieldDismissed.ciudad && proposals.ciudad) setCiudad(proposals.ciudad);
    if (!fieldDismissed.calificacion && proposals.calificacion != null)
      setRating(proposals.calificacion);

    // 2. Queue accepted services
    const acceptedWithIdx = (proposals.services || [])
      .map((svc, originalIdx) => ({ svc, originalIdx }))
      .filter(({ originalIdx }) => serviceAcceptance?.[originalIdx] !== false);
    if (acceptedWithIdx.length > 0) {
      setPendingServices((prev) => {
        const existingIds = new Set(prev.filter((s) => s.serviceId).map((s) => s.serviceId));
        const existingNames = new Set(
          prev.filter((s) => !s.serviceId).map((s) => s.serviceName.toLowerCase()),
        );
        const newSvcs = acceptedWithIdx
          .filter(({ svc }) => {
            if (svc.service_id) return !existingIds.has(svc.service_id);
            return !existingNames.has(svc.service_name.toLowerCase());
          })
          .map(({ svc, originalIdx }) => ({
            localId: uid(),
            serviceName: svc.service_name,
            serviceId: svc.service_id,
            selectedServiceId:
              doc.serviceIdOverride?.[originalIdx] ||
              (svc.service_id ? svc.service_id.toString() : ''),
            description: svc.description,
            costs: svc.costs || [],
            specs: svc.specs || [],
          }));
        return [...prev, ...newSvcs];
      });
    }

    // 3. Save the document as a reference
    if (doc.gcsUrl) {
      const newDoc: SavedDocument = {
        url: doc.gcsUrl,
        name: doc.filename,
        size_bytes: doc.file.size,
        uploaded_at: new Date().toISOString(),
      };
      if (isEdit && id) {
        try {
          const updatedDocs = [...savedDocuments, newDoc];
          await api.patch(`${API_ROUTES.providers}${id}/`, { documents: updatedDocs });
          setSavedDocuments(updatedDocs);
        } catch {
          toast.error('Could not save document reference — will be saved on next Save');
          setSavedDocuments((prev) => [...prev, newDoc]);
        }
      } else {
        setSavedDocuments((prev) => [...prev, newDoc]);
      }
    }

    // 4. Mark as applied (keep visible for review) and mark all fields accepted
    setDocuments((prev) =>
      prev.map((d) => {
        if (d.localId !== doc.localId) return d;
        const updatedAcceptance = { ...d.fieldAcceptance };
        for (const key of Object.keys(updatedAcceptance)) {
          if (!fieldDismissed[key]) updatedAcceptance[key] = true;
        }
        return { ...d, applied: true, reviewOpen: false, fieldAcceptance: updatedAcceptance };
      }),
    );

    const fieldCount = Object.keys(fieldAcceptance || {}).filter((k) => !fieldDismissed[k]).length;
    const svcCount = acceptedWithIdx.length;
    const parts: string[] = [];
    if (fieldCount > 0) parts.push(`${fieldCount} field${fieldCount > 1 ? 's' : ''} applied`);
    if (svcCount > 0) parts.push(`${svcCount} service${svcCount > 1 ? 's' : ''} queued`);
    parts.push('document saved');
    toast.success(parts.join(' · '));
  };

  const handleDismissField = (docLocalId: string, field: string) => {
    setDocuments((prev) =>
      prev.map((d) =>
        d.localId === docLocalId
          ? {
              ...d,
              fieldDismissed: { ...d.fieldDismissed, [field]: true },
              fieldAcceptance: { ...d.fieldAcceptance, [field]: false },
            }
          : d,
      ),
    );
  };

  const handleAcceptService = (doc: DocumentAnalysis, svcIndex: number) => {
    if (!doc.proposals) return;
    const svc = doc.proposals.services[svcIndex];
    setPendingServices((prev) => [
      ...prev,
      {
        localId: uid(),
        serviceName: svc.service_name,
        serviceId: svc.service_id,
        selectedServiceId:
          doc.serviceIdOverride[svcIndex] || (svc.service_id ? svc.service_id.toString() : ''),
        description: svc.description,
        costs: svc.costs,
        specs: svc.specs,
      },
    ]);
    setDocuments((prev) =>
      prev.map((d) =>
        d.localId === doc.localId
          ? { ...d, serviceAcceptance: { ...d.serviceAcceptance, [svcIndex]: false } }
          : d,
      ),
    );
    toast.success('Service queued');
  };

  // ── Similar provider selection ────────────────────────────────

  const handleSelectSimilarProvider = useCallback(
    (provider: SimilarProvider) => {
      // Grab all current analyzed documents to carry over
      const docsToCarry = documents.filter((d) => d.status === 'done');
      navigate(`${ROUTES.dashboardProviders}/${provider.id}/edit`, {
        state: { pendingDocuments: docsToCarry },
      });
    },
    [documents, navigate],
  );

  const handleOpenSimilarModal = useCallback(() => {
    if (similarProviders.length === 0) return;
    openModal(
      <SimilarProvidersModal
        providers={similarProviders}
        onSelectProvider={handleSelectSimilarProvider}
      />,
    );
  }, [similarProviders, handleSelectSimilarProvider, openModal]);

  // ── Image upload ──────────────────────────────────────────────

  const handleImageUpload = useCallback(
    async (files: FileList | File[]) => {
      const imageFiles = Array.from(files).filter((f) => f.type.startsWith('image/'));
      if (imageFiles.length === 0) {
        toast.error('No valid image files selected');
        return;
      }

      setIsUploadingImages(true);
      try {
        const formData = new FormData();
        imageFiles.forEach((f) => formData.append('files', f));
        const res = await api.post(API_ROUTES.uploadProviderImage, formData);
        const uploaded: Array<{ url: string; name: string; size_bytes: number }> =
          res.data.uploaded || [];

        const newImages: SavedImage[] = uploaded.map((u) => ({
          ...u,
          uploaded_at: new Date().toISOString(),
        }));

        const updatedImages = [...savedImages, ...newImages];

        // If provider is already saved, persist immediately
        const pid = isEdit ? id : savedProviderId;
        if (pid) {
          try {
            await api.patch(`${API_ROUTES.providers}${pid}/`, { images: updatedImages });
          } catch {
            toast.error(
              'Images uploaded but could not save to provider — will be saved on next Save',
            );
          }
        }
        setSavedImages(updatedImages);
        toast.success(`${uploaded.length} image${uploaded.length > 1 ? 's' : ''} uploaded`);

        if (res.data.errors?.length) {
          toast.error(`${res.data.errors.length} file(s) failed`);
        }
      } catch {
        toast.error('Failed to upload images');
      } finally {
        setIsUploadingImages(false);
      }
    },
    [savedImages, isEdit, id, savedProviderId],
  );

  const handleRemoveImage = useCallback(
    async (url: string) => {
      const updated = savedImages.filter((img) => img.url !== url);
      setSavedImages(updated);

      const pid = isEdit ? id : savedProviderId;
      if (pid) {
        try {
          await api.patch(`${API_ROUTES.providers}${pid}/`, { images: updated });
        } catch {
          /* will be saved on next Save */
        }
      }
    },
    [savedImages, isEdit, id, savedProviderId],
  );

  // ── Save ──────────────────────────────────────────────────────

  const doSave = useCallback(async () => {
    if (!name.trim()) {
      toast.error('Provider name is required');
      return;
    }
    setIsSaving(true);
    const savedUrls = new Set(savedDocuments.map((sd) => sd.url));
    const docsList: SavedDocument[] = [
      ...savedDocuments,
      ...documents
        .filter((d) => d.status === 'done' && d.gcsUrl && !savedUrls.has(d.gcsUrl))
        .map((d) => ({
          url: d.gcsUrl!,
          name: d.filename,
          size_bytes: d.file.size,
          uploaded_at: new Date().toISOString(),
        })),
    ];
    const payload = {
      nombre_comercial: name,
      contacts: contacts.map((c) => ({
        nombre: c.nombre || null,
        email: c.email || null,
        telefono: c.telefono || null,
        cargo: c.cargo || null,
        es_principal: c.es_principal || false,
        ...(c.id ? { id: c.id } : {}),
      })),
      pais,
      ciudad,
      distrito: distrito || null,
      calificacion: rating || null,
      state: providerState,
      documents: docsList,
      images: savedImages,
    };
    try {
      let providerId: string;
      if (isEdit) {
        await api.patch(`${API_ROUTES.providers}${id}/`, payload);
        providerId = id!;
        toast.success('Provider updated');
        setOriginalValues({ name, contacts, pais, distrito, ciudad, rating, state: providerState });
      } else {
        const res = await api.post(API_ROUTES.providers, payload);
        providerId = res.data.id;
        setSavedProviderId(providerId);
        toast.success('Provider created');
        setOriginalValues({ name, contacts, pais, distrito, ciudad, rating, state: providerState });
      }
      // Deduplicate pending services by resolved service ID
      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      const savedSvcIds = new Set<string>();
      const skippedNames: string[] = [];
      const fallbackPricingUnitId =
        pricingUnits.find((pu) => /\bunit\b/i.test(pu.name) || /\bunidad\b/i.test(pu.name))?.id ||
        pricingUnits[0]?.id ||
        null;
      for (const svc of pendingServices) {
        const svcId = svc.selectedServiceId || svc.serviceId;
        if (!svcId) {
          skippedNames.push(svc.serviceName);
          continue;
        }
        if (savedSvcIds.has(svcId)) continue;
        if (!UUID_RE.test(svcId)) {
          console.error(
            `[offerings] Invalid service UUID for "${svc.serviceName}": "${svcId}" — skipping. ` +
            `selectedServiceId="${svc.selectedServiceId}", serviceId="${svc.serviceId}"`,
          );
          toast.error(`Service "${svc.serviceName}" has an invalid ID and was skipped`);
          continue;
        }
        savedSvcIds.add(svcId);
        try {
          await api.post(API_ROUTES.offerings, {
            provider: providerId,
            service_id: svcId,
            description: svc.description,
            costs: svc.costs
              .filter((c) => c.pricing_unit_id || fallbackPricingUnitId)
              .map((c) => ({
                pricing_unit_id: c.pricing_unit_id || fallbackPricingUnitId,
                price: c.price,
                currency: c.currency,
                description: c.description,
              })),
            specifications: svc.specs
              .filter((s) => s.attribute_id)
              .map((s) => ({ attribute_id: s.attribute_id, value: s.value })),
          });
        } catch (svcErr: unknown) {
          const se = svcErr as { response?: { data?: Record<string, unknown> } };
          const svcData = se.response?.data;
          let svcMsg = `Failed to save service: ${svc.serviceName}`;
          if (svcData) {
            const detail = svcData.non_field_errors || svcData.detail || svcData.error;
            if (Array.isArray(detail)) svcMsg += ` — ${detail[0]}`;
            else if (typeof detail === 'string') svcMsg += ` — ${detail}`;
          }
          toast.error(svcMsg);
        }
      }
      if (skippedNames.length > 0) {
        toast.error(
          `${skippedNames.length} service${skippedNames.length > 1 ? 's' : ''} skipped (no catalog match): ${skippedNames.join(', ')}`,
          { duration: 8000 },
        );
      }
      if (pendingServices.length > 0) {
        setPendingServices([]);
        queryClient.invalidateQueries({ queryKey: ['offerings', providerId] });
      }
      // Mark all remaining analyzed docs as applied after successful save
      const doneDocs = documents.filter((d) => d.status === 'done' && d.gcsUrl && !d.applied);
      if (doneDocs.length > 0) {
        setDocuments((prev) =>
          prev.map((d) => (d.status === 'done' && d.gcsUrl ? { ...d, applied: true } : d)),
        );
      }
      queryClient.invalidateQueries({ queryKey: ['providers'] });
      queryClient.invalidateQueries({ queryKey: ['provider', String(providerId)] });
      queryClient.invalidateQueries({ queryKey: ['provider-stats'] });
      navigate(`${ROUTES.dashboardProviders}/${providerId}`);
    } catch (err: unknown) {
      const e = err as { response?: { data?: Record<string, unknown> } };
      const data = e.response?.data;
      // Extract first field validation error from DRF response
      let msg = 'Error saving provider';
      if (data) {
        if (typeof data.error === 'string') {
          msg = data.error;
        } else if (typeof data.detail === 'string') {
          msg = data.detail;
        } else {
          for (const key of Object.keys(data)) {
            const val = data[key];
            if (Array.isArray(val) && val.length > 0) {
              msg = `${key}: ${val[0]}`;
              break;
            }
            if (typeof val === 'string') {
              msg = `${key}: ${val}`;
              break;
            }
          }
        }
      }
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  }, [
    name,
    contacts,
    ciudad,
    rating,
    providerState,
    savedDocuments,
    savedImages,
    documents,
    isEdit,
    id,
    pendingServices,
    pricingUnits,
    queryClient,
    navigate,
  ]);

  const handleSave = useCallback(async () => {
    const unappliedDocs = documents.filter((d) => d.status === 'done' && !d.applied);
    if (unappliedDocs.length > 0) {
      openModal(
        <div className={styles.unsavedModal}>
          <div className={styles.unsavedModalIcon}>
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </div>
          <h3 className={styles.unsavedModalTitle}>Unapplied Documents</h3>
          <p className={styles.unsavedModalText}>
            You have {unappliedDocs.length} analyzed document{unappliedDocs.length > 1 ? 's' : ''}{' '}
            with proposals that haven't been applied. Their proposed services won't be saved.
          </p>
          <div className={styles.unsavedModalActions}>
            <Button variant="secondary" onClick={() => closeModal()}>
              Cancel
            </Button>
            <Button
              variant="warning"
              onClick={() => {
                closeModal();
                doSave();
              }}
            >
              Save Anyway
            </Button>
          </div>
        </div>,
      );
      return;
    }
    doSave();
  }, [documents, doSave, openModal, closeModal]);

  // ── Unsaved state ─────────────────────────────────────────────
  const hasUnappliedDocs = documents.some((d) => d.status === 'done' && !d.applied);
  const unsaved = hasFormChanges || pendingServices.length > 0 || hasUnappliedDocs;

  // ── React Router navigation blocker ───────────────────────────
  // When the back button's "Discard & Leave" is clicked, we bypass the
  // blocker — otherwise navigate() gets intercepted and nothing happens.
  const discardingRef = useRef(false);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      unsaved && !isSaving && !discardingRef.current && currentLocation.pathname !== nextLocation.pathname,
  );

  // Keep a ref so modal callbacks always access the latest blocker
  const blockerRef = useRef(blocker);
  blockerRef.current = blocker;

  // Intentionally only re-runs when blocker.state changes — adding all deps (blocker,
  // closeModal, doSave, documents, hasUnappliedDocs, openModal, pendingServices) would
  // cause the modal to re-open on every keystroke while navigation is blocked.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    openModal(
      <div className={styles.unsavedModal}>
        <div className={styles.unsavedModalIcon}>
          <FontAwesomeIcon icon={faExclamationTriangle} />
        </div>
        <h3 className={styles.unsavedModalTitle}>Unsaved Changes</h3>
        <p className={styles.unsavedModalText}>
          You have unsaved changes that will be lost if you leave.
          {hasUnappliedDocs &&
            ` You have ${documents.filter((d) => d.status === 'done' && !d.applied).length} analyzed document(s) with unapplied proposals.`}
          {pendingServices.length > 0 &&
            ` ${pendingServices.length} queued service(s) haven't been saved yet.`}
        </p>
        <div className={styles.unsavedModalActions}>
          <Button
            variant="secondary"
            onClick={() => {
              closeModal();
              blockerRef.current.reset?.();
            }}
          >
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={() => {
              closeModal();
              blockerRef.current.proceed?.();
            }}
          >
            Discard & Leave
          </Button>
          <Button
            variant="primary"
            onClick={async () => {
              closeModal();
              await doSave();
              blockerRef.current.proceed?.();
            }}
          >
            Save & Leave
          </Button>
        </div>
      </div>,
    );
  }, [blocker.state]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // ── Ctrl+S keyboard shortcut ──────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleSave]);

  // ── Browser beforeunload guard ────────────────────────────────
  useEffect(() => {
    if (!unsaved) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [unsaved]);

  // ── Render ────────────────────────────────────────────────────
  useEffect(() => {
    setHeaderTitle(
      <div className={styles.headerTitleRow}>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            if (unsaved) {
              openModal(
                <div className={styles.unsavedModal}>
                  <div className={styles.unsavedModalIcon}>
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                  </div>
                  <h3 className={styles.unsavedModalTitle}>Unsaved Changes</h3>
                  <p className={styles.unsavedModalText}>
                    You have unsaved changes that will be lost if you leave.
                    {hasUnappliedDocs &&
                      ` You have ${documents.filter((d) => d.status === 'done' && !d.applied).length} analyzed document(s) with unapplied proposals.`}
                    {pendingServices.length > 0 &&
                      ` ${pendingServices.length} queued service(s) haven't been saved yet.`}
                  </p>
                  <div className={styles.unsavedModalActions}>
                    <Button variant="secondary" onClick={closeModal}>
                      Cancel
                    </Button>
                    <Button
                      variant="warning"
                      onClick={() => {
                        closeModal();
                        discardingRef.current = true;
                        navigate(ROUTES.dashboardProviders);
                      }}
                    >
                      Discard & Leave
                    </Button>
                    <Button
                      variant="primary"
                      onClick={async () => {
                        closeModal();
                        await doSave();
                        if (isEdit) navigate(ROUTES.dashboardProviders);
                      }}
                    >
                      Save & Leave
                    </Button>
                  </div>
                </div>,
              );
            } else {
              navigate(ROUTES.dashboardProviders);
            }
          }}
          className={styles.backBtn}
        >
          <FontAwesomeIcon icon={faArrowLeft} />
        </Button>
        <span>
          {isEdit ? `Edit: ${provider?.nombre_comercial || '…'}` : 'New Provider'}
          {unsaved && <span className={styles.unsavedDot} title="Unsaved changes" />}
        </span>
      </div>,
    );
    setHeaderSlot(
      <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving || !name.trim()}>
        {isSaving ? (
          <FontAwesomeIcon icon={faSpinner} spin />
        ) : (
          <>
            <FontAwesomeIcon icon={faSave} />
            <span className={styles.hideOnMobile}> Save</span>
          </>
        )}
      </Button>,
    );
    return () => {
      setHeaderSlot(null);
      setHeaderTitle(null);
    };
  }, [
    isEdit,
    provider?.nombre_comercial,
    unsaved,
    isSaving,
    name,
    handleSave,
    navigate,
    setHeaderSlot,
    setHeaderTitle,
    hasUnappliedDocs,
    openModal,
    closeModal,
    doSave,
    documents,
    pendingServices,
  ]);

  if (isEdit && isLoadingProvider) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingCenter}>
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className={styles.page}>
      {/* Editor Body */}
      <div className={styles.editorBody}>
        {/* ══════════ LEFT PANEL ══════════ */}
        <div className={styles.formPanel}>
          {/* § Basic Info */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <span>Basic Info</span>
            </div>
            {provider?.temp_creator && (
              <div className={styles.creatorNote}>
                Submitted by: <strong>{provider.temp_creator.name}</strong> (
                {provider.temp_creator.email})
                {provider.temp_creator.country && ` — ${provider.temp_creator.country}`}
              </div>
            )}
            <div className={styles.formGrid}>
              <div className={styles.fieldFull}>
                <Input
                  name="name"
                  label="Provider Name *"
                  value={name}
                  setValue={setName}
                  placeholder="e.g. Hotel Los Delfines"
                />
              </div>
              <Select
                label="Country"
                value={pais}
                onChange={(e) => {
                  setPais(e.target.value);
                  setDistrito('');
                  setCiudad('');
                }}
                options={countryOptions}
                searchable
              />
              <Select
                label="City"
                value={ciudad}
                onChange={(e) => {
                  setCiudad(e.target.value);
                  setDistrito('');
                }}
                options={cityOptions}
                placeholder="Select city..."
                searchable
              />
              <Select
                label="District"
                value={distrito}
                onChange={(e) => setDistrito(e.target.value)}
                options={districtOptions}
                placeholder="Select district..."
                searchable
              />
            </div>
            <div className={styles.ratingStateRow}>
              <div>
                <label className={styles.fieldLabel}>Rating</label>
                <div className={styles.stars}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={styles.starBtn}
                      onClick={() => setRating(n === rating ? 0 : n)}
                    >
                      <FontAwesomeIcon
                        icon={faStar}
                        className={n <= rating ? styles.starFilled : styles.starEmpty}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className={styles.fieldLabel}>Status</label>
                <button
                  type="button"
                  className={styles.switchRoot}
                  onClick={() => setProviderState((s) => (s === 'activo' ? 'borrador' : 'activo'))}
                >
                  <span
                    className={`${styles.switchTrack} ${providerState === 'activo' ? styles.switchOn : styles.switchOff}`}
                  >
                    <span className={styles.switchThumb} />
                  </span>
                  <span
                    className={`${styles.switchLabel} ${providerState === 'activo' ? styles.switchLabelOn : styles.switchLabelOff}`}
                  >
                    {providerState === 'activo' ? 'Active' : 'Draft'}
                  </span>
                </button>
              </div>
            </div>
            <div className={styles.contactsSection}>
              <div className={styles.contactsHeader}>
                <span className={styles.contactsTitle}>Contacts</span>
                <button type="button" className={styles.addContactBtn} onClick={addContact}>
                  <FontAwesomeIcon icon={faPlus} /> Add Contact
                </button>
              </div>
              {contacts.map((contact, idx) => (
                <div key={idx} className={styles.contactRow}>
                  <div className={styles.contactFields}>
                    <Input
                      name={`contact-name-${idx}`}
                      label="Name"
                      value={contact.nombre || ''}
                      setValue={(v) => updateContact(idx, 'nombre', v)}
                      placeholder="Contact person"
                    />
                    <Input
                      name={`contact-email-${idx}`}
                      label="Email"
                      type="email"
                      value={contact.email || ''}
                      setValue={(v) => updateContact(idx, 'email', v)}
                      placeholder="contact@provider.com"
                    />
                    <Input
                      name={`contact-phone-${idx}`}
                      label="Phone"
                      value={contact.telefono || ''}
                      setValue={(v) => updateContact(idx, 'telefono', v)}
                      placeholder="+51 999 000 000"
                    />
                    <Input
                      name={`contact-role-${idx}`}
                      label="Role"
                      value={contact.cargo || ''}
                      setValue={(v) => updateContact(idx, 'cargo', v)}
                      placeholder="e.g. Sales Manager"
                    />
                  </div>
                  <div className={styles.contactActions}>
                    <label className={styles.primaryLabel}>
                      <input
                        type="radio"
                        name="primary-contact"
                        checked={contact.es_principal || false}
                        onChange={() => updateContact(idx, 'es_principal', true)}
                      />
                      Primary
                    </label>
                    {contacts.length > 1 && (
                      <button
                        type="button"
                        className={styles.removeContactBtn}
                        onClick={() => removeContact(idx)}
                        title="Remove contact"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* § Images */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <span>Images</span>
              <button
                className={styles.addServiceBtn}
                onClick={() => imageInputRef.current?.click()}
                disabled={isUploadingImages}
              >
                {isUploadingImages ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin /> Uploading...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPlus} /> Add Images
                  </>
                )}
              </button>
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                multiple
                hidden
                onChange={(e) => {
                  if (e.target.files?.length) {
                    handleImageUpload(e.target.files);
                    e.target.value = '';
                  }
                }}
              />
            </div>

            {savedImages.length > 0 ? (
              <div className={styles.imageGrid}>
                {savedImages.map((img) => (
                  <div key={img.url} className={styles.imageCard}>
                    <img
                      src={img.url}
                      alt={img.name}
                      className={styles.imageThumb}
                      loading="lazy"
                    />
                    <div className={styles.imageOverlay}>
                      <span className={styles.imageName} title={img.name}>
                        {img.name}
                      </span>
                      <button
                        className={styles.imageRemoveBtn}
                        title="Remove"
                        onClick={() => handleRemoveImage(img.url)}
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.emptyServices}>
                <FontAwesomeIcon
                  icon={faImage}
                  style={{ fontSize: 24, opacity: 0.3, marginBottom: 8 }}
                />
                <p>No images yet</p>
                <p className={styles.emptyHint}>Upload photos of the venue, food, etc.</p>
              </div>
            )}
          </section>

          {/* § Services */}
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <span>Services</span>
              {!showServiceForm && (
                <button
                  className={styles.addServiceBtn}
                  onClick={() => {
                    resetServiceForm();
                    setShowServiceForm(true);
                    setTimeout(
                      () =>
                        serviceFormRef.current?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start',
                        }),
                      100,
                    );
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} /> Add Service
                </button>
              )}
            </div>

            {offerings.length > 0 || pendingServices.length > 0 ? (
              <div className={styles.servicesList}>
                {offerings.map((o) => (
                  <div key={o.id} className={styles.serviceCard}>
                    <div className={styles.serviceCardHeader}>
                      <span className={styles.serviceName}>{o.service_details.name}</span>
                      <div className={styles.serviceCardActions}>
                        <button
                          className={styles.iconBtn}
                          title="Edit"
                          onClick={() => startEditOffering(o)}
                        >
                          ✏️
                        </button>
                        <button
                          className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                          title="Delete"
                          onClick={() => deleteOfferingMutation.mutate(o.id)}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                    {o.description && <p className={styles.serviceDesc}>{o.description}</p>}
                    <div className={styles.servicePills}>
                      {o.costs.map((c, i) => (
                        <span key={i} className={styles.costPill}>
                          <FontAwesomeIcon icon={faDollarSign} /> {c.price} {c.currency} /{' '}
                          {c.pricing_unit_details.name}
                        </span>
                      ))}
                      {o.specifications.map((s, i) => (
                        <span key={i} className={styles.specPill}>
                          <FontAwesomeIcon icon={faTag} />{' '}
                          {getAttributeTranslation(s.attribute_details.name)}: {s.value}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
                {pendingServices.map((ps) => {
                  const isExpanded = expandedPending.has(ps.localId);
                  const toggleExpanded = () =>
                    setExpandedPending((prev) => {
                      const next = new Set(prev);
                      if (next.has(ps.localId)) next.delete(ps.localId);
                      else next.add(ps.localId);
                      return next;
                    });
                  return (
                    <div
                      key={ps.localId}
                      className={`${styles.serviceCard} ${styles.pendingServiceCard}`}
                    >
                      <div
                        className={styles.serviceCardHeader}
                        onClick={toggleExpanded}
                        style={{ cursor: 'pointer' }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                          <FontAwesomeIcon
                            icon={isExpanded ? faChevronUp : faChevronDown}
                            style={{
                              fontSize: 10,
                              color: 'var(--color-text-secondary)',
                              flexShrink: 0,
                            }}
                          />
                          <span className={styles.serviceName}>{ps.serviceName}</span>
                        </div>
                        <div
                          className={styles.serviceCardActions}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <span className={styles.pendingBadge}>from document</span>
                          <button
                            className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                            title="Reject service"
                            onClick={() =>
                              setPendingServices((prev) =>
                                prev.filter((p) => p.localId !== ps.localId),
                              )
                            }
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        </div>
                      </div>
                      {ps.serviceId === null && (
                        <div className={styles.unresolvedWarning}>
                          <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            className={styles.warnIcon}
                          />
                          <span>Service not in catalog — select:</span>
                          <select
                            className={styles.inlineSelect}
                            value={ps.selectedServiceId}
                            onChange={(e) =>
                              setPendingServices((prev) =>
                                prev.map((p) =>
                                  p.localId === ps.localId
                                    ? { ...p, selectedServiceId: e.target.value }
                                    : p,
                                ),
                              )
                            }
                          >
                            <option value="">— pick —</option>
                            {catalogServices.map((s) => (
                              <option key={s.id} value={s.id.toString()}>
                                {s.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      {!isExpanded && (
                        <div className={styles.servicePills}>
                          {ps.costs.map((c, i) => (
                            <span key={i} className={styles.costPill}>
                              <FontAwesomeIcon icon={faDollarSign} /> {c.price} {c.currency}
                              {c.unit ? ` / ${c.unit}` : ''}
                            </span>
                          ))}
                          {ps.specs.map((s, i) => (
                            <span key={i} className={styles.specPill}>
                              <FontAwesomeIcon icon={faTag} /> {s.attribute}: {s.value}
                            </span>
                          ))}
                        </div>
                      )}
                      {isExpanded && (
                        <div className={styles.pendingDetails}>
                          {ps.description && <p className={styles.pendingDesc}>{ps.description}</p>}
                          {ps.costs.length > 0 && (
                            <div className={styles.pendingSubsection}>
                              <div className={styles.pendingSubHeader}>Costs</div>
                              {ps.costs.map((c, i) => (
                                <div key={i} className={styles.pendingDetailRow}>
                                  <span>
                                    {c.price} {c.currency}
                                  </span>
                                  {c.unit && (
                                    <span className={styles.pendingDetailUnit}>/ {c.unit}</span>
                                  )}
                                  {c.description && (
                                    <span className={styles.pendingDetailNote}>
                                      {c.description}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {ps.specs.length > 0 && (
                            <div className={styles.pendingSubsection}>
                              <div className={styles.pendingSubHeader}>Specifications</div>
                              {ps.specs.map((s, i) => (
                                <div key={i} className={styles.pendingDetailRow}>
                                  <span className={styles.pendingDetailAttr}>{s.attribute}</span>
                                  <span>{s.value}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className={styles.emptyServices}>
                <p>No services yet.</p>
                <p className={styles.emptyHint}>
                  Upload a document or click "Add Service" to begin.
                </p>
              </div>
            )}

            {/* Inline service form */}
            {showServiceForm && (
              <div ref={serviceFormRef} className={styles.serviceForm}>
                <div className={styles.serviceFormHeader}>
                  <span>{editingOfferingId ? 'Edit Service' : 'Add Service'}</span>
                  <button className={styles.iconBtn} onClick={resetServiceForm}>
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
                <div className={styles.serviceSelectRow}>
                  <Select
                    label="Service"
                    value={serviceForm.serviceId}
                    onChange={(e) => setServiceForm((f) => ({ ...f, serviceId: e.target.value }))}
                    options={[
                      { value: '', label: '— select —' },
                      ...catalogServices.map((s) => ({ value: s.id.toString(), label: s.name })),
                    ]}
                    searchable
                  />
                  <button
                    type="button"
                    className={styles.inlineCreateBtn}
                    onClick={() => setIsCreatingService((c) => !c)}
                  >
                    <FontAwesomeIcon icon={faPlus} /> New
                  </button>
                </div>
                {isCreatingService && (
                  <div className={styles.inlineCreate}>
                    <Input
                      name="newServiceName"
                      label="Service name"
                      value={newServiceName}
                      setValue={setNewServiceName}
                      placeholder="e.g. Catering"
                    />
                    <Select
                      label="Category (opt.)"
                      value={newServiceCategory}
                      onChange={(e) => setNewServiceCategory(e.target.value)}
                      options={[
                        { value: '', label: '— none —' },
                        ...categories.map((c) => ({ value: c.id.toString(), label: c.nombre })),
                      ]}
                    />
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setIsCreatingService(false);
                        setNewServiceName('');
                        setNewServiceCategory('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => createServiceMutation.mutate()}
                      disabled={!newServiceName.trim()}
                    >
                      Create
                    </Button>
                  </div>
                )}
                <Input
                  name="serviceDesc"
                  label="Description"
                  value={serviceForm.description}
                  setValue={(v) => setServiceForm((f) => ({ ...f, description: v }))}
                  placeholder="Briefly describe this service…"
                />

                <div className={styles.subsectionHeader}>
                  <FontAwesomeIcon icon={faDollarSign} /> Costs
                  <button type="button" className={styles.addRowBtn} onClick={addCost}>
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
                {serviceForm.costs.map((c) => (
                  <div key={c.localId} className={styles.costRow}>
                    <Select
                      label="Unit"
                      value={c.pricingUnitId}
                      onChange={(e) => updateCost(c.localId, 'pricingUnitId', e.target.value)}
                      options={[
                        { value: '', label: '— unit —' },
                        ...pricingUnits.map((u) => ({ value: u.id.toString(), label: u.name })),
                      ]}
                    />
                    <Input
                      name={`price-${c.localId}`}
                      label="Price"
                      type="number"
                      value={c.price}
                      setValue={(v) => updateCost(c.localId, 'price', v)}
                      placeholder="0.00"
                    />
                    <Input
                      name={`costdesc-${c.localId}`}
                      label="Note"
                      value={c.description}
                      setValue={(v) => updateCost(c.localId, 'description', v)}
                      placeholder="Optional"
                      multiline
                      rows={2}
                    />
                    <button
                      className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                      onClick={() => removeCost(c.localId)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))}
                <button type="button" className={styles.addRowBtnBottom} onClick={addCost}>
                  <FontAwesomeIcon icon={faPlus} /> Add Cost
                </button>

                <div className={styles.subsectionHeader}>
                  <FontAwesomeIcon icon={faTag} /> Specifications
                  <button type="button" className={styles.addRowBtn} onClick={addSpec}>
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
                {serviceForm.specs.map((s, idx) => (
                  <div key={s.localId} className={styles.specRow}>
                    <div className={styles.attrSelectWrap}>
                      <Select
                        label="Attribute"
                        value={s.attributeId}
                        onChange={(e) => updateSpec(s.localId, 'attributeId', e.target.value)}
                        options={[
                          { value: '', label: '— attr —' },
                          ...allAttributes.map((a) => ({
                            value: a.id.toString(),
                            label: getAttributeTranslation(a.name),
                          })),
                        ]}
                        searchable
                      />
                      <button
                        type="button"
                        className={styles.inlineCreateBtn}
                        onClick={() => {
                          setCreatingAttrIndex(idx);
                          setNewAttrName('');
                        }}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                      </button>
                    </div>
                    {creatingAttrIndex === idx && (
                      <div className={styles.inlineCreate}>
                        <Input
                          name="newAttrName"
                          label="New attribute"
                          value={newAttrName}
                          setValue={setNewAttrName}
                          placeholder="Attribute name"
                        />
                        <Button
                          size="sm"
                          onClick={() => createAttributeMutation.mutate()}
                          disabled={!newAttrName.trim()}
                        >
                          Create
                        </Button>
                      </div>
                    )}
                    <Input
                      name={`specval-${s.localId}`}
                      label="Value"
                      value={s.value}
                      setValue={(v) => updateSpec(s.localId, 'value', v)}
                      placeholder="e.g. 200"
                      multiline
                      rows={2}
                    />
                    <button
                      className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                      onClick={() => removeSpec(s.localId)}
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))}
                <button type="button" className={styles.addRowBtnBottom} onClick={addSpec}>
                  <FontAwesomeIcon icon={faPlus} /> Add Specification
                </button>

                <div className={styles.serviceFormActions}>
                  <Button variant="secondary" size="sm" onClick={resetServiceForm}>
                    Cancel
                  </Button>
                  <Button variant="primary" size="sm" onClick={handleSubmitServiceForm}>
                    {editingOfferingId ? 'Update' : 'Add'}
                  </Button>
                </div>
              </div>
            )}
          </section>
        </div>

        {/* ══════════ RIGHT PANEL (AI SIDEBAR) ══════════ */}
        {(sidebarExpanded || mobileSidebarOpen) && (
          <div
            className={styles.sidebarOverlay}
            onClick={() => {
              setSidebarExpanded(false);
              setMobileSidebarOpen(false);
            }}
          />
        )}

        <div
          className={`${styles.aiSidebar} ${sidebarExpanded ? styles.aiSidebarExpanded : ''} ${mobileSidebarOpen ? styles.aiSidebarMobileOpen : ''}`}
        >
          {/* Sidebar header with expand toggle */}
          <div className={styles.sidebarHeader}>
            <span className={styles.sidebarTitle}>
              <FontAwesomeIcon icon={faRobot} /> AI Document Analysis
            </span>
            <div className={styles.sidebarHeaderActions}>
              <button
                className={styles.sidebarExpandBtn}
                onClick={() => setSidebarExpanded((prev) => !prev)}
                title={sidebarExpanded ? 'Collapse panel' : 'Expand panel for better review'}
              >
                <FontAwesomeIcon icon={sidebarExpanded ? faCompress : faExpand} />
                <span className={styles.sidebarExpandLabel}>
                  {sidebarExpanded ? 'Collapse' : 'Expand'}
                </span>
              </button>
              <button
                className={styles.sidebarCloseBtn}
                onClick={() => setMobileSidebarOpen(false)}
                title="Close panel"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>

          {/* § Document Upload */}
          <section className={styles.sidebarSection}>
            <div className={styles.sidebarSectionHeader}>Documents &amp; AI</div>
            <div
              className={`${styles.dropZone} ${isDragOver ? styles.dropZoneActive : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragOver(false);
                handleFilesAdded(e.dataTransfer.files);
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <FontAwesomeIcon icon={faUpload} className={styles.dropIcon} />
              <span className={styles.dropText}>Drop files or click to browse</span>
              <span className={styles.dropHint}>
                PDF, DOCX, TXT, PNG, JPG, WEBP · max 50 MB · up to 20 files
              </span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.docx,.txt,.png,.jpg,.jpeg,.webp"
              style={{ display: 'none' }}
              onChange={(e) => {
                if (e.target.files) handleFilesAdded(e.target.files);
                e.target.value = '';
              }}
            />
            <textarea
              className={styles.contextArea}
              value={contextText}
              onChange={(e) => setContextText(e.target.value)}
              placeholder="Optional AI context (e.g. 'Prices for Lima 2026 events')"
              rows={2}
            />
          </section>

          {/* § Similar providers warning */}
          {!isEdit && similarProviders.length > 0 && (
            <div className={styles.similarBanner} onClick={handleOpenSimilarModal}>
              <FontAwesomeIcon icon={faExclamationTriangle} className={styles.similarBannerIcon} />
              <span>
                {similarProviders.length} similar provider{similarProviders.length > 1 ? 's' : ''}{' '}
                found
              </span>
            </div>
          )}

          {/* § Analyzed Documents */}
          {documents.length > 0 && (
            <section className={styles.sidebarSection}>
              <div className={styles.sidebarSectionHeader}>Analyzed Documents</div>
              <div className={styles.docList}>
                {documents.map((doc) => (
                  <DocumentCard
                    key={doc.localId}
                    doc={doc}
                    isEdit={isEdit}
                    currentValues={{ name, pais, ciudad, distrito, rating }}
                    catalogServices={catalogServices}
                    onToggleFieldAcceptance={(field) =>
                      setDocuments((prev) =>
                        prev.map((d) =>
                          d.localId === doc.localId
                            ? {
                                ...d,
                                fieldAcceptance: {
                                  ...d.fieldAcceptance,
                                  [field]: !d.fieldAcceptance[field],
                                },
                              }
                            : d,
                        ),
                      )
                    }
                    onDismissField={(field) => handleDismissField(doc.localId, field)}
                    onToggleServiceAcceptance={(idx) =>
                      setDocuments((prev) =>
                        prev.map((d) =>
                          d.localId === doc.localId
                            ? {
                                ...d,
                                serviceAcceptance: {
                                  ...d.serviceAcceptance,
                                  [idx]: !d.serviceAcceptance[idx],
                                },
                              }
                            : d,
                        ),
                      )
                    }
                    onServiceIdOverride={(idx, val) =>
                      setDocuments((prev) =>
                        prev.map((d) =>
                          d.localId === doc.localId
                            ? { ...d, serviceIdOverride: { ...d.serviceIdOverride, [idx]: val } }
                            : d,
                        ),
                      )
                    }
                    onApplyField={handleApplyField}
                    onApplyAll={() => handleApplyAll(doc)}
                    onAcceptService={(idx) => handleAcceptService(doc, idx)}
                    onRemove={() =>
                      setDocuments((prev) => prev.filter((d) => d.localId !== doc.localId))
                    }
                    onRetry={() => analyzeDocument(doc.localId, doc.file)}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Saved documents (edit mode) */}
          {savedDocuments.length > 0 && (
            <section className={styles.sidebarSection}>
              <div className={styles.sidebarSectionHeader}>Previously Uploaded</div>
              <div className={styles.savedDocList}>
                {savedDocuments.map((sd) => (
                  <div key={sd.url} className={styles.savedDocItem}>
                    <FontAwesomeIcon icon={faFile} className={styles.savedDocIcon} />
                    <span className={styles.savedDocName} title={sd.name}>
                      {sd.name}
                    </span>
                    <button
                      className={styles.iconBtn}
                      title="Remove"
                      onClick={() =>
                        setSavedDocuments((prev) => prev.filter((doc) => doc.url !== sd.url))
                      }
                    >
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>

      {/* ══════════ MOBILE FAB ══════════ */}
      <button
        className={styles.mobileFab}
        onClick={() => setMobileSidebarOpen(true)}
        aria-label="Open AI document sidebar"
      >
        <FontAwesomeIcon icon={faRobot} />
        {documents.length > 0 && (
          <span className={styles.mobileFabBadge}>{documents.length}</span>
        )}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────
// DocumentCard sub-component
// ─────────────────────────────────────────────

interface DocumentCardProps {
  doc: DocumentAnalysis;
  isEdit: boolean;
  currentValues: { name: string; pais: string; ciudad: string; distrito: string; rating: number };
  catalogServices: CatalogService[];
  onToggleFieldAcceptance: (field: string) => void;
  onDismissField: (field: string) => void;
  onToggleServiceAcceptance: (idx: number) => void;
  onServiceIdOverride: (idx: number, val: string) => void;
  onApplyField: (field: string, value: string | number | null) => void;
  onApplyAll: () => void;
  onAcceptService: (idx: number) => void;
  onRemove: () => void;
  onRetry: () => void;
}

function DocumentCard({
  doc,
  isEdit,
  currentValues,
  catalogServices,
  onToggleFieldAcceptance,
  onDismissField,
  onToggleServiceAcceptance,
  onServiceIdOverride,
  onApplyField,
  onApplyAll,
  onAcceptService,
  onRemove,
  onRetry,
}: DocumentCardProps) {
  const [expandedServices, setExpandedServices] = useState<Record<number, boolean>>({});
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (doc.status !== 'uploading' && doc.status !== 'analyzing') return;
    const start = doc.analysisStartTime || Date.now();
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [doc.status, doc.analysisStartTime]);

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const FIELD_LABELS: Record<string, string> = {
    nombre_comercial: 'Name',
    pais: 'Country',
    distrito: 'District',
    ciudad: 'City',
    calificacion: 'Rating',
  };
  const currentValMap: Record<string, string | number> = {
    nombre_comercial: currentValues.name,
    pais: currentValues.pais,
    distrito: currentValues.distrito,
    ciudad: currentValues.ciudad,
    calificacion: currentValues.rating,
  };

  if (doc.status === 'uploading' || doc.status === 'analyzing') {
    const phases = [
      { label: 'Uploading to cloud', icon: faCloudArrowUp, done: doc.status === 'analyzing' },
      {
        label: 'Extracting content',
        icon: faFileLines,
        done: doc.status === 'analyzing' && elapsed >= 3,
      },
      { label: 'AI analyzing document', icon: faRobot, done: false },
      { label: 'Building proposals', icon: faClipboardCheck, done: false },
    ];
    return (
      <div className={styles.docCard}>
        <div className={styles.analysisProgress}>
          <div className={styles.analysisHeader}>
            <span className={styles.docCardFilename}>{doc.filename}</span>
            <span className={styles.analysisTimer}>{formatTime(elapsed)}</span>
          </div>
          <div className={styles.phaseList}>
            {phases.map((phase, i) => {
              const isActive = !phase.done && (i === 0 || phases[i - 1].done);
              return (
                <div
                  key={i}
                  className={`${styles.phase} ${phase.done ? styles.phaseDone : ''} ${isActive ? styles.phaseActive : ''}`}
                >
                  <div className={styles.phaseIcon}>
                    {phase.done ? (
                      <FontAwesomeIcon icon={faCheck} />
                    ) : isActive ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <FontAwesomeIcon icon={phase.icon} />
                    )}
                  </div>
                  <span className={styles.phaseLabel}>{phase.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  if (doc.status === 'error') {
    return (
      <div className={`${styles.docCard} ${styles.docCardError}`}>
        <div className={styles.docCardErrorRow}>
          <FontAwesomeIcon icon={faExclamationTriangle} className={styles.errorIcon} />
          <div style={{ flex: 1 }}>
            <div className={styles.docCardFilename}>{doc.filename}</div>
            <div className={styles.docCardStatus}>{doc.error || 'Analysis failed'}</div>
          </div>
          <div className={styles.docCardActions}>
            <button className={styles.cardBtn} onClick={onRetry} title="Retry">
              <FontAwesomeIcon icon={faArrowsRotate} />
            </button>
            <button className={`${styles.cardBtn} ${styles.cardBtnDanger}`} onClick={onRemove}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // status === 'done'
  const proposalFieldCount = Object.keys(doc.fieldAcceptance || {}).filter(
    (k) => !doc.fieldDismissed?.[k],
  ).length;
  const proposalSvcCount = (doc.proposals?.services || []).length;

  return (
    <div
      className={`${styles.docCard} ${doc.applied ? styles.docCardApplied : styles.docCardDone}`}
    >
      <div className={styles.docCardDoneRow}>
        <FontAwesomeIcon
          icon={faCheckCircle}
          className={doc.applied ? styles.appliedIcon : styles.doneIcon}
        />
        <div className={styles.docCardInfo}>
          <div className={styles.docCardFilename}>
            {doc.filename}
            {doc.applied && <span className={styles.appliedBadge}>Applied</span>}
          </div>
          <div className={styles.docCardSummary}>{doc.summary}</div>
        </div>
        <div className={styles.docCardActions}>
          <button className={`${styles.cardBtn} ${styles.cardBtnDanger}`} onClick={onRemove}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      </div>
      {!doc.applied && (
        <button className={styles.applyAllBtn} onClick={onApplyAll}>
          <FontAwesomeIcon icon={faCheck} />
          Apply All Proposals
          {proposalFieldCount + proposalSvcCount > 0 && (
            <span className={styles.applyAllCount}>
              {proposalFieldCount} fields + {proposalSvcCount} services
            </span>
          )}
        </button>
      )}

      {doc.proposals && (
        <div className={styles.reviewSection}>
          {/* Basic field proposals */}
          <div className={styles.reviewFieldList}>
            {Object.entries(FIELD_LABELS).map(([key, label]) => {
              const proposed = doc.proposals![key as keyof ProviderProposal] as
                | string
                | number
                | null
                | undefined;
              if (proposed == null || proposed === '') return null;
              if (doc.fieldDismissed?.[key]) return null;
              const accepted = !!doc.fieldAcceptance?.[key];
              return (
                <div key={key} className={styles.reviewFieldRow}>
                  <div className={styles.reviewFieldLabel}>{label}</div>
                  {isEdit ? (
                    <div className={styles.diffRow}>
                      <div className={styles.diffCurrent}>
                        <span className={styles.diffBadge}>Current</span>
                        <span>{String(currentValMap[key] || '—')}</span>
                      </div>
                      <div className={styles.diffProposed}>
                        <span className={styles.diffBadge}>Proposed</span>
                        <span>{String(proposed)}</span>
                      </div>
                      <div className={styles.diffBtns}>
                        <button
                          className={styles.diffBtn}
                          onClick={() => onApplyField(key, proposed)}
                        >
                          Use Proposed
                        </button>
                        <button
                          className={styles.diffBtnSecondary}
                          onClick={() => onDismissField(key)}
                          title="Keep current, dismiss proposal"
                        >
                          Keep Current
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.createModeField}>
                      <span className={styles.proposedVal}>{String(proposed)}</span>
                      <button
                        className={`${styles.fieldToggleBtn} ${accepted ? styles.fieldToggleBtnOn : ''}`}
                        onClick={() => {
                          onToggleFieldAcceptance(key);
                          if (!accepted) onApplyField(key, proposed);
                        }}
                      >
                        {accepted ? (
                          <>
                            <FontAwesomeIcon icon={faCheck} /> Applied
                          </>
                        ) : (
                          'Apply'
                        )}
                      </button>
                      <button
                        className={styles.fieldDismissBtn}
                        onClick={() => onDismissField(key)}
                        title="Dismiss this proposal"
                      >
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Service proposals */}
          {(doc.proposals.services || []).length > 0 && (
            <div className={styles.reviewServices}>
              <div className={styles.reviewServicesHeader}>Proposed Services</div>
              {(doc.proposals.services || []).map((svc, idx) => {
                const accepted = doc.serviceAcceptance?.[idx] !== false;
                const expanded = !!expandedServices[idx];
                return (
                  <div
                    key={idx}
                    className={`${styles.reviewServiceCard} ${!accepted ? styles.reviewServiceSkipped : ''}`}
                  >
                    <div className={styles.reviewServiceHeader}>
                      <div
                        className={styles.reviewServiceNameRow}
                        onClick={() =>
                          setExpandedServices((prev) => ({ ...prev, [idx]: !prev[idx] }))
                        }
                        style={{ cursor: 'pointer' }}
                      >
                        <FontAwesomeIcon
                          icon={expanded ? faChevronUp : faChevronDown}
                          className={styles.serviceExpandIcon}
                        />
                        <span className={styles.reviewServiceName}>{svc.service_name}</span>
                        {svc.matched ? (
                          <span className={`${styles.matchBadge} ${styles.matchBadgeGreen}`}>
                            <FontAwesomeIcon icon={faCheckCircle} /> matched
                          </span>
                        ) : (
                          <span className={`${styles.matchBadge} ${styles.matchBadgeYellow}`}>
                            <FontAwesomeIcon icon={faExclamationTriangle} /> unmatched
                          </span>
                        )}
                      </div>
                      <div className={styles.reviewServiceBtns}>
                        {accepted ? (
                          <>
                            <button
                              className={`${styles.reviewServiceBtn} ${styles.reviewServiceBtnApply}`}
                              onClick={() => onAcceptService(idx)}
                            >
                              <FontAwesomeIcon icon={faCheck} /> Queue
                            </button>
                            <button
                              className={`${styles.reviewServiceBtn} ${styles.reviewServiceBtnSkip}`}
                              onClick={() => onToggleServiceAcceptance(idx)}
                              title="Skip this service"
                            >
                              <FontAwesomeIcon icon={faTimes} /> Skip
                            </button>
                          </>
                        ) : (
                          <button
                            className={`${styles.reviewServiceBtn} ${styles.reviewServiceBtnRestore}`}
                            onClick={() => onToggleServiceAcceptance(idx)}
                          >
                            <FontAwesomeIcon icon={faArrowsRotate} /> Restore
                          </button>
                        )}
                      </div>
                    </div>
                    {!svc.matched && accepted && (
                      <div className={styles.unresolvedWarning}>
                        <FontAwesomeIcon icon={faExclamationTriangle} className={styles.warnIcon} />
                        <span>Pick catalog service:</span>
                        <select
                          className={styles.inlineSelect}
                          value={doc.serviceIdOverride?.[idx] || ''}
                          onChange={(e) => onServiceIdOverride(idx, e.target.value)}
                        >
                          <option value="">— select —</option>
                          {catalogServices.map((s) => (
                            <option key={s.id} value={s.id.toString()}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {expanded && (
                      <div className={styles.serviceDetailExpanded}>
                        {svc.description && (
                          <div className={styles.serviceDetailItem}>
                            <span className={styles.serviceDetailLabel}>Description</span>
                            <span className={styles.serviceDetailValue}>{svc.description}</span>
                          </div>
                        )}
                        {(svc.costs || []).length > 0 && (
                          <div className={styles.serviceDetailItem}>
                            <span className={styles.serviceDetailLabel}>Costs</span>
                            <div className={styles.serviceDetailRows}>
                              {(svc.costs || []).map((c, i) => (
                                <div key={i} className={styles.serviceDetailRow}>
                                  <FontAwesomeIcon
                                    icon={faDollarSign}
                                    className={styles.serviceDetailIcon}
                                  />
                                  <span className={styles.serviceDetailMain}>
                                    {c.price} {c.currency} / {c.unit || '—'}
                                  </span>
                                  {c.description && (
                                    <span className={styles.serviceDetailNote}>
                                      {c.description}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {(svc.specs || []).length > 0 && (
                          <div className={styles.serviceDetailItem}>
                            <span className={styles.serviceDetailLabel}>Specifications</span>
                            <div className={styles.serviceDetailRows}>
                              {(svc.specs || []).map((s, i) => (
                                <div key={i} className={styles.serviceDetailRow}>
                                  <FontAwesomeIcon
                                    icon={faTag}
                                    className={styles.serviceDetailIcon}
                                  />
                                  <span className={styles.serviceDetailMain}>
                                    {getAttributeTranslation(s.attribute || '')}: {s.value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
